import { lazy, Suspense, useState, useEffect, useMemo, useRef, useCallback } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import {
  Sparkle,
  Clock,
  CaretRight,
  CaretLeft,
  ArrowsLeftRight,
  CircleNotch,
} from '@phosphor-icons/react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from '@/components/ui/carousel'
import { ManufacturerBrandLogo } from '@/components/ManufacturerBrandLogo'
import { StorefrontHomeHeading } from '@/components/StorefrontHomeHeading'
import { usePricingContext } from '@/components/pricing/PricingContext'
import { useStorefrontSiteConfig } from '@/contexts/site-config'
import { ProductCard } from './ProductCard'
import { ProductCardSkeleton } from './ProductCardSkeleton'
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll'
import { VIEW_PATHS, buildCategoryHref, buildProductHref, handleLinkClick } from '@/lib/navigation'
import { filterUnavailableProductsWhenStockRuleEnabled, isProductUnavailable, sortProductsByAvailability } from '@/lib/product-stock'
import { isProductDisplayedPricePending, resolveProductPricing } from '@/lib/pricing'
import { slugify } from '@/lib/slug'
import { trackStorefrontImportantClick } from '@/lib/storefront-analytics'
import { getStorefrontManufacturerBrands, type StorefrontManufacturerBrand } from '@/lib/api-shell'
import { formatCategoryLabel, getCategoryVisual } from '@/lib/category-visual'
import { selectHomeComboProducts } from '@/lib/home-offer-products'
import { orderProductsForShowcase } from '@/lib/showcase-product-order'
import { hexToRgba, lightenHexColor, resolveAccessibleTint } from '@/lib/storefront-theme'
import { cn } from '@/lib/utils'
import type { Category, CategorySelectionData, Product } from '@/lib/types'
import { useTranslation } from 'react-i18next'
import { formatCommerceCurrency, useBuyerCommerceContext } from '@/lib/commerce-context'

interface HomeViewProps {
  categories: Category[]
  featuredProducts: Product[]
  featuredProductsView?: 'launches' | 'products'
  justArrivedProducts: Product[]
  onCategoryClick: (categoryId: string, categoryData?: CategorySelectionData) => void
  onProductClick: (product: Product) => void
  onAddToCart: (product: Product) => void
  onManufacturerBrandClick?: (brand: StorefrontManufacturerBrand) => void
  onViewChange?: (view: 'launches' | 'just_arrived' | 'products') => void
  isLoggedIn?: boolean
  allowGuestCheckout?: boolean
  recommendationEntidadeId?: number | null
  recommendationRefreshKey?: string | number | boolean | null
  isProductsLoading?: boolean
  isCategoriesLoading?: boolean
  deferredSectionsReady?: boolean
}

const LAUNCH_ITEMS_PER_LINE = 6
const LAUNCH_MAX_ROWS = 4
const LAUNCH_MAX_ITEMS = LAUNCH_ITEMS_PER_LINE * LAUNCH_MAX_ROWS
const LAUNCH_FEED_INITIAL_ITEMS = 12
const OFFER_SECTION_ITEMS = 10
const INITIAL_RAIL_ITEMS = 4
const HOME_RAIL_CAROUSEL_ITEM_CLASS = 'flex flex-col pl-2.5 basis-1/2 sm:basis-[206px] xl:basis-[220px] 2xl:basis-[228px]'
const CONTROL_FEEDBACK_DURATION_MS = 220
const OFFER_CAROUSEL_AUTOPLAY_MS = 3500
const CATEGORY_SCROLL_STEP_RATIO = 0.82
const CATEGORY_SCROLL_EDGE_TOLERANCE_PX = 8

type CarouselDirection = 'prev' | 'next'

const updateBooleanState = (
  setter: Dispatch<SetStateAction<boolean>>,
  nextValue: boolean
) => {
  setter((current) => current === nextValue ? current : nextValue)
}

const HomeRecommendationsSection = lazy(() =>
  import('./HomeRecommendationsSection').then((module) => ({ default: module.HomeRecommendationsSection }))
)

function HomeDeferredSectionSkeleton() {
  return (
    <section
      className="pb-12"
      style={{ contentVisibility: 'auto', containIntrinsicSize: '1120px' }}
    >
      <div className="container mx-auto px-4">
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
          <div className="rounded-[28px] border border-border/60 bg-card/80 p-5 shadow-sm">
            <div className="space-y-3">
              <Skeleton className="h-7 w-56" />
              <Skeleton className="h-4 w-80 max-w-full" />
            </div>
            <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <ProductCardSkeleton key={`home-deferred-recommendation-${index}`} viewMode="grid" flushImage />
              ))}
            </div>
          </div>

          <div className="rounded-[28px] border border-border/60 bg-card/80 p-5 shadow-sm">
            <div className="space-y-3">
              <Skeleton className="h-7 w-48" />
              <Skeleton className="h-4 w-64 max-w-full" />
            </div>
            <div className="mt-5 grid grid-cols-2 gap-3 xl:grid-cols-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <ProductCardSkeleton key={`home-deferred-feed-${index}`} viewMode="grid" flushImage />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function ManufacturerBrandCircle({
  brand,
  onClick,
}: {
  brand: StorefrontManufacturerBrand
  onClick?: (brand: StorefrontManufacturerBrand) => void
}) {
  return (
    <button
      type="button"
      onClick={() => onClick?.(brand)}
      className="group flex h-full w-full items-center justify-center rounded-full p-1 transition-all"
      aria-label={brand.name}
      title={brand.name}
    >
      <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-full border border-border/60 bg-card/70 p-2 transition-all duration-200 group-hover:border-border group-hover:bg-card group-hover:shadow-sm sm:h-[4.5rem] sm:w-[4.5rem] sm:p-2.5">
        <ManufacturerBrandLogo
          brandName={brand.name}
          logoUrl={brand.logoUrl}
          imgClassName="scale-100 transition-transform duration-200 group-hover:scale-105"
          fallbackClassName="text-xs tracking-[0.14em]"
        />
      </div>
    </button>
  )
}

const uniqueByProductId = (products: Product[]) => {
  const seenIds = new Set<string>()

  return products.filter((product) => {
    if (seenIds.has(product.id)) return false
    seenIds.add(product.id)
    return true
  })
}

const collectShowcaseBrandsFromProducts = (
  products: Product[],
  limit: number
): StorefrontManufacturerBrand[] => {
  const counts = new Map<number, number>()
  const brands = new Map<number, StorefrontManufacturerBrand>()

  for (const product of products) {
    const rawId = Number(product.manufacturerBrandId ?? 0)
    if (!Number.isFinite(rawId) || rawId <= 0) continue

    const id = Math.trunc(rawId)
    const name = String(product.manufacturerBrandName ?? '').trim()
    const logoUrl = typeof product.manufacturerBrandLogoUrl === 'string'
      ? product.manufacturerBrandLogoUrl.trim()
      : ''

    if (!name) continue

    counts.set(id, (counts.get(id) ?? 0) + 1)

    if (!brands.has(id)) {
      brands.set(id, {
        id,
        name,
        logoUrl: logoUrl || null,
        productCount: 0,
      })
    }
  }

  return Array.from(brands.values())
    .sort((left, right) => {
      const countDifference = (counts.get(right.id) ?? 0) - (counts.get(left.id) ?? 0)
      if (countDifference !== 0) return countDifference

      return left.name.localeCompare(right.name, 'pt-BR', { sensitivity: 'base' })
    })
    .slice(0, limit)
    .map((brand) => ({
      ...brand,
      productCount: counts.get(brand.id) ?? 0,
    }))
}

export function HomeView({
  categories,
  featuredProducts,
  featuredProductsView = 'launches',
  justArrivedProducts,
  onCategoryClick,
  onProductClick,
  onAddToCart,
  onManufacturerBrandClick,
  onViewChange,
  isLoggedIn = false,
  allowGuestCheckout = false,
  recommendationEntidadeId = null,
  recommendationRefreshKey = null,
  isProductsLoading = false,
  isCategoriesLoading = false,
  deferredSectionsReady = false,
}: HomeViewProps) {
  useBuyerCommerceContext()
  const { t } = useTranslation()
  const pricingContext = usePricingContext()
  const { showBrandCarousel, themeColors, seoTitle } = useStorefrontSiteConfig()
  const fallbackShowcaseBrands = useMemo(
    () => collectShowcaseBrandsFromProducts(
      [...featuredProducts, ...justArrivedProducts],
      18
    ),
    [featuredProducts, justArrivedProducts]
  )
  const [isInitialLoading, setIsInitialLoading] = useState(true)
  const [comboDirection, setComboDirection] = useState<CarouselDirection | null>(null)
  const [superDirection, setSuperDirection] = useState<CarouselDirection | null>(null)
  const [categoriesDirection, setCategoriesDirection] = useState<CarouselDirection | null>(null)
  const [isCategoriesOverflowing, setIsCategoriesOverflowing] = useState(false)
  const [canScrollCategoriesPrev, setCanScrollCategoriesPrev] = useState(false)
  const [canScrollCategoriesNext, setCanScrollCategoriesNext] = useState(false)
  const [comboCarouselApi, setComboCarouselApi] = useState<CarouselApi | null>(null)
  const [superCarouselApi, setSuperCarouselApi] = useState<CarouselApi | null>(null)
  const [canScrollComboPrev, setCanScrollComboPrev] = useState(false)
  const [canScrollComboNext, setCanScrollComboNext] = useState(false)
  const [canScrollSuperPrev, setCanScrollSuperPrev] = useState(false)
  const [canScrollSuperNext, setCanScrollSuperNext] = useState(false)
  const [canScrollBrandPrev, setCanScrollBrandPrev] = useState(false)
  const [canScrollBrandNext, setCanScrollBrandNext] = useState(false)
  const [showcaseBrands, setShowcaseBrands] = useState<StorefrontManufacturerBrand[]>(
    () => showBrandCarousel ? fallbackShowcaseBrands : []
  )
  const comboFeedbackTimerRef = useRef<number | null>(null)
  const superFeedbackTimerRef = useRef<number | null>(null)
  const categoriesFeedbackTimerRef = useRef<number | null>(null)
  const categoriesScrollRef = useRef<HTMLDivElement | null>(null)
  const brandScrollRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    let active = true

    if (!showBrandCarousel) {
      setShowcaseBrands([])
      return () => {
        active = false
      }
    }

    if (fallbackShowcaseBrands.length > 0) {
      setShowcaseBrands((current) => current.length > 0 ? current : fallbackShowcaseBrands)
    }

    getStorefrontManufacturerBrands({ onlyWithLogo: true, limit: 18 })
      .then((items) => {
        if (!active) return
        if (items.length > 0) {
          setShowcaseBrands(items)
          return
        }

        if (fallbackShowcaseBrands.length === 0) {
          setShowcaseBrands([])
        }
      })
      .catch((error) => {
        if (!active) return
        console.warn('Failed to load storefront manufacturer brands:', error)
        if (fallbackShowcaseBrands.length === 0) {
          setShowcaseBrands([])
        }
      })

    return () => {
      active = false
    }
  }, [fallbackShowcaseBrands, showBrandCarousel])

  useEffect(() => {
    if (!showBrandCarousel || fallbackShowcaseBrands.length === 0) return

    setShowcaseBrands((current) => current.length > 0 ? current : fallbackShowcaseBrands)
  }, [fallbackShowcaseBrands, showBrandCarousel])

  const resolveDisplayPrice = useCallback((product: Product) => {
    if (isProductUnavailable(product, pricingContext.stockStatusMode)) return null

    const pricing = resolveProductPricing(product, pricingContext)
    return isProductDisplayedPricePending(product, pricingContext, pricing)
      ? null
      : pricing.actualPrice
  }, [pricingContext])

  const formatCurrency = (value: number | null) => {
    if (typeof value !== 'number' || value <= 0) return '-'
    return formatCommerceCurrency(value)
  }

  const getOfferSectionChipLabel = (lowestPrice: number | null) =>
    isLoggedIn && typeof lowestPrice === 'number' && lowestPrice > 0
      ? t('home.offer_sections.combo_chip', { price: formatCurrency(lowestPrice) })
      : t('home.offer_sections.view_all')

  const handleHeroBannerClick = useCallback((target: 'launches' | 'just_arrived' | 'products') => {
    trackStorefrontImportantClick({
      eventKey: 'hero_banner_click',
      pageKind: 'home',
      pageKey: target,
      pagePath: VIEW_PATHS.home,
      payload: {
        target,
      },
    })
    onViewChange?.(target)
  }, [onViewChange])

  const handleMainCategoryClick = useCallback((category: Category) => {
    const categorySlug = category.slug || slugify(category.name)
    trackStorefrontImportantClick({
      eventKey: 'main_category_click',
      pageKind: 'home',
      pageKey: categorySlug,
      pagePath: VIEW_PATHS.home,
      payload: {
        category_id: category.id,
        category_name: category.name,
      },
    })
    onCategoryClick(category.id, {
      name: category.name,
      path: [{
        id: category.id,
        name: category.name,
        slug: categorySlug,
      }],
    })
  }, [onCategoryClick])

  const triggerComboFeedback = useCallback((direction: CarouselDirection) => {
    setComboDirection(direction)

    if (comboFeedbackTimerRef.current) {
      window.clearTimeout(comboFeedbackTimerRef.current)
    }

    comboFeedbackTimerRef.current = window.setTimeout(() => {
      setComboDirection(null)
      comboFeedbackTimerRef.current = null
    }, CONTROL_FEEDBACK_DURATION_MS)
  }, [])

  const triggerSuperFeedback = useCallback((direction: CarouselDirection) => {
    setSuperDirection(direction)

    if (superFeedbackTimerRef.current) {
      window.clearTimeout(superFeedbackTimerRef.current)
    }

    superFeedbackTimerRef.current = window.setTimeout(() => {
      setSuperDirection(null)
      superFeedbackTimerRef.current = null
    }, CONTROL_FEEDBACK_DURATION_MS)
  }, [])

  const triggerCategoriesFeedback = useCallback((direction: CarouselDirection) => {
    setCategoriesDirection(direction)

    if (categoriesFeedbackTimerRef.current) {
      window.clearTimeout(categoriesFeedbackTimerRef.current)
    }

    categoriesFeedbackTimerRef.current = window.setTimeout(() => {
      setCategoriesDirection(null)
      categoriesFeedbackTimerRef.current = null
    }, CONTROL_FEEDBACK_DURATION_MS)
  }, [])

  const uniqueFeaturedProducts = useMemo(
    () => sortProductsByAvailability(
      filterUnavailableProductsWhenStockRuleEnabled(uniqueByProductId(featuredProducts), pricingContext.stockStatusMode),
      pricingContext.stockStatusMode
    ),
    [featuredProducts, pricingContext.stockStatusMode]
  )
  const uniqueJustArrivedProducts = useMemo(
    () => sortProductsByAvailability(
      filterUnavailableProductsWhenStockRuleEnabled(uniqueByProductId(justArrivedProducts), pricingContext.stockStatusMode),
      pricingContext.stockStatusMode
    ),
    [justArrivedProducts, pricingContext.stockStatusMode]
  )
  const recommendationFallbackProducts = useMemo(
    () => uniqueByProductId([...uniqueFeaturedProducts, ...uniqueJustArrivedProducts]),
    [uniqueFeaturedProducts, uniqueJustArrivedProducts]
  )
  const superOfferProducts = useMemo(
    () => orderProductsForShowcase(uniqueJustArrivedProducts, 'just-arrived').slice(0, OFFER_SECTION_ITEMS),
    [uniqueJustArrivedProducts]
  )
  const { comboProducts, comboProductIds } = useMemo(() => {
    const combo = selectHomeComboProducts(
      uniqueFeaturedProducts,
      superOfferProducts,
      featuredProductsView,
      OFFER_SECTION_ITEMS
    )

    return {
      comboProducts: combo,
      comboProductIds: new Set(combo.map((product) => product.id)),
    }
  }, [featuredProductsView, superOfferProducts, uniqueFeaturedProducts])
  const comboTargetView = featuredProductsView === 'launches' ? 'launches' : 'products'
  const comboTargetHref = comboTargetView === 'launches' ? VIEW_PATHS.launches : VIEW_PATHS.products

  const launchFeedProducts = useMemo(() => {
    if (!deferredSectionsReady) {
      return []
    }

    const remainingProducts = uniqueFeaturedProducts.filter((product) => !comboProductIds.has(product.id))
    const remainingProductIds = new Set(remainingProducts.map((product) => product.id))
    const backfillProducts = uniqueFeaturedProducts.filter((product) => !remainingProductIds.has(product.id))
    const prioritizedProducts = remainingProducts.length >= LAUNCH_FEED_INITIAL_ITEMS
      ? remainingProducts
      : [...remainingProducts, ...backfillProducts]
    return sortProductsByAvailability(
      filterUnavailableProductsWhenStockRuleEnabled(
        orderProductsForShowcase(prioritizedProducts, 'launch-feed'),
        pricingContext.stockStatusMode
      ),
      pricingContext.stockStatusMode
    )
  }, [comboProductIds, deferredSectionsReady, pricingContext.stockStatusMode, uniqueFeaturedProducts])
  const limitedLaunchFeedProducts = useMemo(
    () => launchFeedProducts.slice(0, LAUNCH_MAX_ITEMS),
    [launchFeedProducts]
  )
  const visibleComboProducts = useMemo(
    () => comboProducts,
    [comboProducts]
  )
  const visibleSuperOfferProducts = useMemo(
    () => superOfferProducts,
    [superOfferProducts]
  )
  const hasComboOffers = comboProducts.length > 0
  const hasSuperOffers = superOfferProducts.length > 0
  const isOfferSectionsLoading = isInitialLoading || isProductsLoading
  const showComboOfferSection = hasComboOffers || isOfferSectionsLoading
  const showSuperOfferSection = hasSuperOffers || isOfferSectionsLoading

  const allowTouchOnlyRailDrag = useCallback((_: CarouselApi, event: MouseEvent | TouchEvent) => {
    return !(event instanceof MouseEvent)
  }, [])
  const productRailCarouselOpts = useMemo(() => ({
    align: 'start' as const,
    containScroll: 'trimSnaps' as const,
    slidesToScroll: 1,
    watchDrag: allowTouchOnlyRailDrag,
  }), [allowTouchOnlyRailDrag])
  const handleSetComboCarouselApi = useCallback((api: CarouselApi) => {
    setComboCarouselApi((current) => current === api ? current : api)
  }, [])
  const handleSetSuperCarouselApi = useCallback((api: CarouselApi) => {
    setSuperCarouselApi((current) => current === api ? current : api)
  }, [])

  const launchSectionStyle = useMemo(() => {
    const sectionColor = themeColors.launches
    const chipColors = resolveAccessibleTint(sectionColor, 0.08)
    return {
      card: {
        borderColor: hexToRgba(sectionColor, 0.22),
        background: `linear-gradient(135deg, ${hexToRgba(lightenHexColor(sectionColor, 0.42), 0.92)} 0%, ${hexToRgba(lightenHexColor(sectionColor, 0.3), 0.95)} 55%, ${hexToRgba(lightenHexColor(sectionColor, 0.16), 0.98)} 100%)`,
        boxShadow: `0 18px 45px -30px ${hexToRgba(sectionColor, 0.75)}`,
      },
      glowTop: {
        backgroundColor: hexToRgba(sectionColor, 0.15),
      },
      glowBottom: {
        backgroundColor: hexToRgba(lightenHexColor(sectionColor, 0.35), 0.5),
      },
      chip: {
        borderColor: hexToRgba(sectionColor, 0.3),
        ...chipColors,
      },
      control: {
        borderColor: hexToRgba(sectionColor, 0.24),
      },
      controlActive: {
        borderColor: sectionColor,
        backgroundColor: hexToRgba(sectionColor, 0.1),
        boxShadow: `0 0 0 2px ${hexToRgba(sectionColor, 0.2)}`,
      },
    }
  }, [themeColors.launches])

  const justArrivedSectionStyle = useMemo(() => {
    const sectionColor = themeColors.justArrived
    const chipColors = resolveAccessibleTint(sectionColor, 0.08)
    return {
      card: {
        borderColor: hexToRgba(sectionColor, 0.24),
        background: `linear-gradient(135deg, ${hexToRgba(lightenHexColor(sectionColor, 0.46), 0.92)} 0%, ${hexToRgba(lightenHexColor(sectionColor, 0.34), 0.95)} 55%, ${hexToRgba(lightenHexColor(sectionColor, 0.2), 0.98)} 100%)`,
        boxShadow: `0 18px 45px -30px ${hexToRgba(sectionColor, 0.75)}`,
      },
      glowTop: {
        backgroundColor: hexToRgba(sectionColor, 0.14),
      },
      glowBottom: {
        backgroundColor: hexToRgba(lightenHexColor(sectionColor, 0.3), 0.45),
      },
      chip: {
        borderColor: hexToRgba(sectionColor, 0.3),
        ...chipColors,
      },
      control: {
        borderColor: hexToRgba(sectionColor, 0.24),
      },
      controlActive: {
        borderColor: sectionColor,
        backgroundColor: hexToRgba(sectionColor, 0.1),
        boxShadow: `0 0 0 2px ${hexToRgba(sectionColor, 0.2)}`,
      },
    }
  }, [themeColors.justArrived])

  const goToPreviousComboPage = useCallback(() => {
    if (!comboCarouselApi?.canScrollPrev()) return
    triggerComboFeedback('prev')
    comboCarouselApi.scrollPrev()
  }, [comboCarouselApi, triggerComboFeedback])

  const goToNextComboPage = useCallback(() => {
    if (!comboCarouselApi?.canScrollNext()) return
    triggerComboFeedback('next')
    comboCarouselApi.scrollNext()
  }, [comboCarouselApi, triggerComboFeedback])

  const goToPreviousSuperPage = useCallback(() => {
    if (!superCarouselApi?.canScrollPrev()) return
    triggerSuperFeedback('prev')
    superCarouselApi.scrollPrev()
  }, [superCarouselApi, triggerSuperFeedback])

  const goToNextSuperPage = useCallback(() => {
    if (!superCarouselApi?.canScrollNext()) return
    triggerSuperFeedback('next')
    superCarouselApi.scrollNext()
  }, [superCarouselApi, triggerSuperFeedback])

  const goToPreviousBrandPage = useCallback(() => {
    const container = brandScrollRef.current
    if (!container || !canScrollBrandPrev) return

    const scrollStep = Math.max(container.clientWidth * CATEGORY_SCROLL_STEP_RATIO, 180)
    container.scrollBy({
      left: -scrollStep,
      behavior: 'smooth',
    })
  }, [canScrollBrandPrev])

  const goToNextBrandPage = useCallback(() => {
    const container = brandScrollRef.current
    if (!container || !canScrollBrandNext) return

    const scrollStep = Math.max(container.clientWidth * CATEGORY_SCROLL_STEP_RATIO, 180)
    container.scrollBy({
      left: scrollStep,
      behavior: 'smooth',
    })
  }, [canScrollBrandNext])

  const syncCategoriesScrollState = useCallback(() => {
    const container = categoriesScrollRef.current
    if (!container) {
      updateBooleanState(setIsCategoriesOverflowing, false)
      updateBooleanState(setCanScrollCategoriesPrev, false)
      updateBooleanState(setCanScrollCategoriesNext, false)
      return
    }

    const maxScrollLeft = Math.max(0, container.scrollWidth - container.clientWidth)
    const hasOverflow = maxScrollLeft > CATEGORY_SCROLL_EDGE_TOLERANCE_PX
    const scrollLeft = Math.max(0, Math.min(container.scrollLeft, maxScrollLeft))

    updateBooleanState(setIsCategoriesOverflowing, hasOverflow)
    updateBooleanState(setCanScrollCategoriesPrev, hasOverflow && scrollLeft > CATEGORY_SCROLL_EDGE_TOLERANCE_PX)
    updateBooleanState(setCanScrollCategoriesNext, hasOverflow && scrollLeft < maxScrollLeft - CATEGORY_SCROLL_EDGE_TOLERANCE_PX)
  }, [])

  const syncBrandScrollState = useCallback(() => {
    const container = brandScrollRef.current
    if (!container) {
      updateBooleanState(setCanScrollBrandPrev, false)
      updateBooleanState(setCanScrollBrandNext, false)
      return
    }

    const maxScrollLeft = Math.max(0, container.scrollWidth - container.clientWidth)
    const hasOverflow = maxScrollLeft > CATEGORY_SCROLL_EDGE_TOLERANCE_PX
    const scrollLeft = Math.max(0, Math.min(container.scrollLeft, maxScrollLeft))

    updateBooleanState(setCanScrollBrandPrev, hasOverflow && scrollLeft > CATEGORY_SCROLL_EDGE_TOLERANCE_PX)
    updateBooleanState(setCanScrollBrandNext, hasOverflow && scrollLeft < maxScrollLeft - CATEGORY_SCROLL_EDGE_TOLERANCE_PX)
  }, [])

  const scrollCategories = useCallback((direction: CarouselDirection) => {
    const container = categoriesScrollRef.current
    if (!container) return

    triggerCategoriesFeedback(direction)

    const scrollStep = Math.max(container.clientWidth * CATEGORY_SCROLL_STEP_RATIO, 180)

    container.scrollBy({
      left: direction === 'next' ? scrollStep : -scrollStep,
      behavior: 'smooth',
    })
  }, [triggerCategoriesFeedback])

  const comboLowestPrice = useMemo(() => {
    const values = comboProducts
      .map((product) => resolveDisplayPrice(product))
      .filter((value): value is number => typeof value === 'number' && value > 0)
    if (values.length === 0) return null
    return Math.min(...values)
  }, [comboProducts, resolveDisplayPrice])
  const superOfferLowestPrice = useMemo(() => {
    const values = superOfferProducts
      .map((product) => resolveDisplayPrice(product))
      .filter((value): value is number => typeof value === 'number' && value > 0)
    if (values.length === 0) return null
    return Math.min(...values)
  }, [superOfferProducts, resolveDisplayPrice])
  const {
    displayedItems: visibleLaunchProducts,
    hasMore: hasMoreLaunchProducts,
    observerTarget: launchObserverTarget,
    isLoadingMore: isLoadingMoreLaunchProducts,
  } = useInfiniteScroll(limitedLaunchFeedProducts, LAUNCH_FEED_INITIAL_ITEMS, {
    threshold: 0.15,
    rootMargin: '220px',
    enabled: deferredSectionsReady,
  })
  const showCategoryControls = isCategoriesOverflowing && !isCategoriesLoading && !isOfferSectionsLoading

  useEffect(() => {
    if (isProductsLoading) {
      setIsInitialLoading(true)
      return
    }

    const timer = setTimeout(() => {
      setIsInitialLoading(false)
    }, 280)

    return () => clearTimeout(timer)
  }, [isProductsLoading])

  useEffect(() => {
    return () => {
      if (comboFeedbackTimerRef.current) {
        window.clearTimeout(comboFeedbackTimerRef.current)
      }
      if (superFeedbackTimerRef.current) {
        window.clearTimeout(superFeedbackTimerRef.current)
      }
      if (categoriesFeedbackTimerRef.current) {
        window.clearTimeout(categoriesFeedbackTimerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!comboCarouselApi) {
      updateBooleanState(setCanScrollComboPrev, false)
      updateBooleanState(setCanScrollComboNext, false)
      return
    }

    const syncState = () => {
      updateBooleanState(setCanScrollComboPrev, comboCarouselApi.canScrollPrev())
      updateBooleanState(setCanScrollComboNext, comboCarouselApi.canScrollNext())
    }

    syncState()
    comboCarouselApi.on('select', syncState)
    comboCarouselApi.on('reInit', syncState)

    return () => {
      comboCarouselApi.off('select', syncState)
      comboCarouselApi.off('reInit', syncState)
    }
  }, [comboCarouselApi, visibleComboProducts.length])

  useEffect(() => {
    if (!comboCarouselApi || isOfferSectionsLoading || visibleComboProducts.length <= 1) return

    const intervalId = window.setInterval(() => {
      if (comboCarouselApi.canScrollNext()) {
        comboCarouselApi.scrollNext()
        return
      }

      comboCarouselApi.scrollTo(0)
    }, OFFER_CAROUSEL_AUTOPLAY_MS)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [comboCarouselApi, isOfferSectionsLoading, visibleComboProducts.length])

  useEffect(() => {
    if (!superCarouselApi) {
      updateBooleanState(setCanScrollSuperPrev, false)
      updateBooleanState(setCanScrollSuperNext, false)
      return
    }

    const syncState = () => {
      updateBooleanState(setCanScrollSuperPrev, superCarouselApi.canScrollPrev())
      updateBooleanState(setCanScrollSuperNext, superCarouselApi.canScrollNext())
    }

    syncState()
    superCarouselApi.on('select', syncState)
    superCarouselApi.on('reInit', syncState)

    return () => {
      superCarouselApi.off('select', syncState)
      superCarouselApi.off('reInit', syncState)
    }
  }, [superCarouselApi, visibleSuperOfferProducts.length])

  useEffect(() => {
    if (!superCarouselApi || isOfferSectionsLoading || visibleSuperOfferProducts.length <= 1) return

    const intervalId = window.setInterval(() => {
      if (superCarouselApi.canScrollNext()) {
        superCarouselApi.scrollNext()
        return
      }

      superCarouselApi.scrollTo(0)
    }, OFFER_CAROUSEL_AUTOPLAY_MS)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [superCarouselApi, isOfferSectionsLoading, visibleSuperOfferProducts.length])

  useEffect(() => {
    if (showcaseBrands.length === 0) {
      updateBooleanState(setCanScrollBrandPrev, false)
      updateBooleanState(setCanScrollBrandNext, false)
      return
    }

    const container = brandScrollRef.current
    if (!container) return

    const handleScroll = () => {
      syncBrandScrollState()
    }

    const resizeObserver = new ResizeObserver(() => {
      syncBrandScrollState()
    })

    container.addEventListener('scroll', handleScroll, { passive: true })
    resizeObserver.observe(container)

    const track = container.firstElementChild
    if (track instanceof HTMLElement) {
      resizeObserver.observe(track)
    }

    const frameId = window.requestAnimationFrame(() => {
      syncBrandScrollState()
    })

    return () => {
      window.cancelAnimationFrame(frameId)
      container.removeEventListener('scroll', handleScroll)
      resizeObserver.disconnect()
    }
  }, [showcaseBrands.length, syncBrandScrollState])

  useEffect(() => {
    if (isCategoriesLoading || isOfferSectionsLoading) {
      updateBooleanState(setIsCategoriesOverflowing, false)
      updateBooleanState(setCanScrollCategoriesPrev, false)
      updateBooleanState(setCanScrollCategoriesNext, false)
      return
    }

    const container = categoriesScrollRef.current
    if (!container) return

    const handleScroll = () => {
      syncCategoriesScrollState()
    }

    const resizeObserver = new ResizeObserver(() => {
      syncCategoriesScrollState()
    })

    container.addEventListener('scroll', handleScroll, { passive: true })
    resizeObserver.observe(container)

    const track = container.firstElementChild
    if (track instanceof HTMLElement) {
      resizeObserver.observe(track)
    }

    const frameId = window.requestAnimationFrame(() => {
      syncCategoriesScrollState()
    })

    return () => {
      window.cancelAnimationFrame(frameId)
      container.removeEventListener('scroll', handleScroll)
      resizeObserver.disconnect()
    }
  }, [categories, isCategoriesLoading, isOfferSectionsLoading, syncCategoriesScrollState])

  return (
    <div className="min-h-screen bg-background">
      <StorefrontHomeHeading title={seoTitle} />
      <div className="container mx-auto px-4 pt-8 pb-3 space-y-10">
        {(showBrandCarousel && showcaseBrands.length > 0) ? (
          <section className="pt-1">
            <div className="relative px-10 sm:px-12">
              <div className="absolute inset-y-0 left-0 z-10 flex items-center">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={goToPreviousBrandPage}
                  disabled={showcaseBrands.length === 0 || !canScrollBrandPrev}
                  className="rounded-full bg-background/90 shadow-sm"
                  aria-label={t('actions.previous')}
                >
                  <CaretLeft size={20} />
                </Button>
              </div>

              <div
                ref={brandScrollRef}
                className="overflow-x-auto scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              >
                <div className="-ml-2.5 flex min-w-max snap-x snap-mandatory">
                  {showcaseBrands.map((brand) => (
                    <div
                      key={brand.id}
                      className="w-[84px] flex-shrink-0 snap-start pl-2.5 sm:w-[96px] md:w-[104px] lg:w-[112px]"
                    >
                      <ManufacturerBrandCircle
                        brand={brand}
                        onClick={onManufacturerBrandClick}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="absolute inset-y-0 right-0 z-10 flex items-center">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={goToNextBrandPage}
                  disabled={showcaseBrands.length === 0 || !canScrollBrandNext}
                  className="rounded-full bg-background/90 shadow-sm"
                  aria-label={t('actions.next')}
                >
                  <CaretRight size={20} />
                </Button>
              </div>
            </div>
          </section>
        ) : null}

        {(showComboOfferSection || showSuperOfferSection) && (
          <section>
            <div className={`grid gap-4 ${
              showComboOfferSection && showSuperOfferSection
                ? 'xl:grid-cols-2'
                : 'xl:grid-cols-1'
            }`}>
              {showComboOfferSection && (
                <Card className="relative overflow-hidden border p-4 md:p-5" style={launchSectionStyle.card}>
                  <div className="pointer-events-none absolute -right-20 -top-20 h-52 w-52 rounded-full blur-3xl" style={launchSectionStyle.glowTop} />
                  <div className="pointer-events-none absolute -bottom-24 -left-16 h-52 w-52 rounded-full blur-3xl" style={launchSectionStyle.glowBottom} />
                  <div className="relative z-10">
                    {isOfferSectionsLoading ? (
                      <div className="flex items-center justify-between gap-3">
                        <div className="h-7 w-48 animate-pulse rounded-md" style={{ backgroundColor: hexToRgba(themeColors.launches, 0.15) }} />
                        <div className="h-9 w-48 rounded-full border bg-white/70 animate-pulse" style={launchSectionStyle.control} />
                      </div>
                    ) : (
                        <div className="flex items-center justify-between gap-2 sm:gap-3">
                        <h2 className="flex min-w-0 items-center gap-1.5 text-lg font-semibold text-foreground sm:gap-2 sm:text-xl">
                          <Sparkle className="h-5 w-5 sm:h-6 sm:w-6" weight="fill" style={{ color: themeColors.launches }} />
                          {t('home.offer_sections.combo_title')}
                        </h2>
                        <a
                          href={comboTargetHref}
                          onClick={(event) => handleLinkClick(event, () => handleHeroBannerClick(comboTargetView))}
                          className="inline-flex shrink-0 items-center gap-1.5 rounded-full border bg-white/75 px-2.5 py-1.5 text-[13px] font-semibold backdrop-blur transition-colors hover:bg-white sm:gap-2 sm:px-3 sm:text-sm"
                          style={launchSectionStyle.chip}
                        >
                          <Sparkle className="h-4 w-4 sm:h-5 sm:w-5" weight="fill" style={{ color: themeColors.launches }} />
                          <span className="whitespace-nowrap">{getOfferSectionChipLabel(comboLowestPrice)}</span>
                          <CaretRight className="h-3.5 w-3.5 text-foreground/70 sm:h-4 sm:w-4" />
                        </a>
                      </div>
                    )}

                    <div className="relative mt-4">
                      <div className="absolute inset-y-0 -left-4 z-10 hidden items-center md:flex">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className={`h-8 w-8 rounded-md bg-white/85 transition-all duration-200 ${
                            comboDirection === 'prev' ? 'scale-110' : ''
                          }`}
                          style={comboDirection === 'prev' ? launchSectionStyle.controlActive : launchSectionStyle.control}
                          disabled={isOfferSectionsLoading || !canScrollComboPrev}
                          onClick={goToPreviousComboPage}
                          aria-label={t('actions.previous')}
                        >
                          <CaretLeft className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="absolute inset-y-0 -right-4 z-10 hidden items-center md:flex">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className={`h-8 w-8 rounded-md bg-white/85 transition-all duration-200 ${
                            comboDirection === 'next' ? 'scale-110' : ''
                          }`}
                          style={comboDirection === 'next' ? launchSectionStyle.controlActive : launchSectionStyle.control}
                          disabled={isOfferSectionsLoading || !canScrollComboNext}
                          onClick={goToNextComboPage}
                          aria-label={t('actions.next')}
                        >
                          <CaretRight className="h-4 w-4" />
                        </Button>
                      </div>

                      {isOfferSectionsLoading ? (
                        <div className="flex gap-3 overflow-hidden">
                          {Array.from({ length: 4 }).map((_, index) => (
                            <div key={`combo-skeleton-${index}`} className="w-[calc((100%-0.75rem)/2)] shrink-0 sm:w-[206px] xl:w-[220px] 2xl:w-[228px]">
                              <ProductCardSkeleton viewMode="grid" flushImage />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <Carousel
                          className="w-full"
                          setApi={handleSetComboCarouselApi}
                          opts={productRailCarouselOpts}
                        >
                          <CarouselContent className="-ml-2.5 pb-2">
                            {visibleComboProducts.map((product, index) => (
                              <CarouselItem
                                key={`combo-${product.id}-${index}`}
                                className={HOME_RAIL_CAROUSEL_ITEM_CLASS}
                              >
                                <ProductCard
                                  product={product}
                                  onClick={() => onProductClick(product)}
                                  onAddToCart={() => onAddToCart(product)}
                                  linkHref={buildProductHref(product)}
                                  maxNameLines={2}
                                  flushImage
                                  showRatings
                                  showStockBadge
                                  hideLoginBadge
                                  priorityImage={index === 0}
                                  viewMode="grid"
                                  isLoggedIn={isLoggedIn}
                                  allowGuestCheckout={allowGuestCheckout}
                                />
                              </CarouselItem>
                            ))}
                          </CarouselContent>
                        </Carousel>
                      )}
                    </div>
                  </div>
                </Card>
              )}

              {showSuperOfferSection && (
                <Card className="relative overflow-hidden border p-4 md:p-5" style={justArrivedSectionStyle.card}>
                <div className="pointer-events-none absolute -left-16 -top-20 h-52 w-52 rounded-full blur-3xl" style={justArrivedSectionStyle.glowTop} />
                <div className="pointer-events-none absolute -bottom-24 -right-16 h-52 w-52 rounded-full blur-3xl" style={justArrivedSectionStyle.glowBottom} />
                <div className="relative z-10">
                  {isOfferSectionsLoading ? (
                    <div className="flex items-center justify-between gap-3">
                      <div className="h-7 w-48 animate-pulse rounded-md" style={{ backgroundColor: hexToRgba(themeColors.justArrived, 0.15) }} />
                      <div className="h-9 w-48 rounded-full border bg-white/70 animate-pulse" style={justArrivedSectionStyle.control} />
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-2 sm:gap-3">
                      <h2 className="flex min-w-0 items-center gap-1.5 text-lg font-semibold text-foreground sm:gap-2 sm:text-xl">
                        <Clock className="h-5 w-5 sm:h-6 sm:w-6" weight="bold" style={{ color: themeColors.justArrived }} />
                        {t('header.quick_links.just_arrived')}
                      </h2>
                      <a
                        href={VIEW_PATHS.just_arrived}
                        onClick={(event) => handleLinkClick(event, () => handleHeroBannerClick('just_arrived'))}
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-full border bg-white/75 px-2.5 py-1.5 text-[13px] font-semibold backdrop-blur transition-colors hover:bg-white sm:gap-2 sm:px-3 sm:text-sm"
                        style={justArrivedSectionStyle.chip}
                      >
                        <Clock className="h-4 w-4 sm:h-5 sm:w-5" weight="bold" style={{ color: themeColors.justArrived }} />
                        <span className="whitespace-nowrap">{getOfferSectionChipLabel(superOfferLowestPrice)}</span>
                        <CaretRight className="h-3.5 w-3.5 text-foreground/70 sm:h-4 sm:w-4" />
                      </a>
                    </div>
                  )}

                  <div
                    className="relative mt-4"
                  >
                    <div className="absolute inset-y-0 -left-4 z-10 hidden items-center md:flex">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className={`h-8 w-8 rounded-md bg-white/85 transition-all duration-200 ${
                          superDirection === 'prev' ? 'scale-110' : ''
                        }`}
                        style={superDirection === 'prev' ? justArrivedSectionStyle.controlActive : justArrivedSectionStyle.control}
                        disabled={isOfferSectionsLoading || !canScrollSuperPrev}
                        onClick={goToPreviousSuperPage}
                        aria-label={t('actions.previous')}
                      >
                        <CaretLeft className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="absolute inset-y-0 -right-4 z-10 hidden items-center md:flex">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className={`h-8 w-8 rounded-md bg-white/85 transition-all duration-200 ${
                          superDirection === 'next' ? 'scale-110' : ''
                        }`}
                        style={superDirection === 'next' ? justArrivedSectionStyle.controlActive : justArrivedSectionStyle.control}
                        disabled={isOfferSectionsLoading || !canScrollSuperNext}
                        onClick={goToNextSuperPage}
                        aria-label={t('actions.next')}
                      >
                        <CaretRight className="h-4 w-4" />
                      </Button>
                    </div>

                    {isOfferSectionsLoading ? (
                      <div className="flex gap-3 overflow-hidden">
                        {Array.from({ length: 4 }).map((_, index) => (
                          <div key={`super-skeleton-${index}`} className="w-[calc((100%-0.75rem)/2)] shrink-0 sm:w-[206px] xl:w-[220px] 2xl:w-[228px]">
                            <ProductCardSkeleton viewMode="grid" flushImage />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <Carousel
                        className="w-full"
                        setApi={handleSetSuperCarouselApi}
                        opts={productRailCarouselOpts}
                      >
                        <CarouselContent className="-ml-2.5 pb-2">
                          {visibleSuperOfferProducts.map((product, index) => (
                            <CarouselItem
                              key={`super-${product.id}-${index}`}
                              className={HOME_RAIL_CAROUSEL_ITEM_CLASS}
                            >
                              <ProductCard
                                product={product}
                                onClick={() => onProductClick(product)}
                                onAddToCart={() => onAddToCart(product)}
                                linkHref={buildProductHref(product)}
                                maxNameLines={2}
                                flushImage
                                showRatings
                                showStockBadge
                                hideLoginBadge
                                priorityImage={!hasComboOffers && index === 0}
                                viewMode="grid"
                                isLoggedIn={isLoggedIn}
                                allowGuestCheckout={allowGuestCheckout}
                              />
                            </CarouselItem>
                          ))}
                        </CarouselContent>
                      </Carousel>
                    )}
                  </div>
                </div>
              </Card>
            )}
            </div>
          </section>
        )}

        <section className="min-h-[9.5rem] py-1 sm:min-h-[10.25rem]">
          <div className="space-y-3">
            {showCategoryControls ? (
              <div className="flex min-h-10 justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => scrollCategories('prev')}
                  disabled={!canScrollCategoriesPrev}
                  className={cn(
                    'rounded-full',
                    categoriesDirection === 'prev' && 'border-primary/50 bg-primary/5'
                  )}
                  aria-label={t('actions.previous')}
                >
                  <CaretLeft size={20} />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => scrollCategories('next')}
                  disabled={!canScrollCategoriesNext}
                  className={cn(
                    'rounded-full',
                    categoriesDirection === 'next' && 'border-primary/50 bg-primary/5'
                  )}
                  aria-label={t('actions.next')}
                >
                  <CaretRight size={20} />
                </Button>
              </div>
            ) : null}

            <div
              ref={categoriesScrollRef}
              className="scrollbar-hidden overflow-x-auto scroll-smooth snap-x snap-mandatory pb-2"
            >
              <div
                className="mx-auto flex w-max min-w-full justify-center gap-4 sm:gap-5"
              >
                {isCategoriesLoading || isOfferSectionsLoading
                  ? Array.from({ length: 5 }).map((_, index) => (
                      <div key={`category-skeleton-${index}`} className="w-[112px] sm:w-[120px] flex-shrink-0">
                        <div className="flex flex-col items-center gap-2 text-center">
                          <div className="h-20 w-20 rounded-full border border-border/60 bg-muted/60 animate-pulse sm:h-24 sm:w-24" />
                          <div className="h-3.5 w-24 rounded bg-muted/60 animate-pulse" />
                          <div className="h-3.5 w-16 rounded bg-muted/40 animate-pulse" />
                        </div>
                      </div>
                    ))
                  : categories.map((category) => {
                      const categoryVisual = getCategoryVisual({ descricao: category.name, slug: category.slug })

                      return (
                        <a
                          key={category.id}
                          href={buildCategoryHref([category.slug || slugify(category.name)])}
                          onClick={(event) =>
                            handleLinkClick(event, () => handleMainCategoryClick(category))
                          }
                          className="group w-[112px] sm:w-[120px] flex-shrink-0 snap-start"
                        >
                          <div className="flex flex-col items-center gap-2 text-center">
                            <div className="flex h-20 w-20 items-center justify-center rounded-full border border-border/70 bg-card transition-all group-hover:border-primary/50 group-hover:bg-primary/5 group-hover:shadow-sm sm:h-24 sm:w-24">
                              {categoryVisual.type === 'image' ? (
                                <img
                                  src={categoryVisual.src}
                                  alt={categoryVisual.alt}
                                  width={56}
                                  height={56}
                                  className="h-11 w-11 object-contain transition-transform group-hover:scale-105 sm:h-14 sm:w-14"
                                  loading="lazy"
                                  decoding="async"
                                  draggable={false}
                                />
                              ) : (
                                <categoryVisual.icon className="h-8 w-8 text-foreground/65 transition-colors group-hover:text-primary sm:h-9 sm:w-9" />
                              )}
                            </div>
                            <h3 className="line-clamp-2 min-h-[2.5rem] text-sm font-medium leading-tight text-foreground/90">
                              {formatCategoryLabel(category.name)}
                            </h3>
                          </div>
                        </a>
                      )
                    })}
              </div>
            </div>
          </div>
        </section>
      </div>

      {!deferredSectionsReady && !isOfferSectionsLoading && (
        <HomeDeferredSectionSkeleton />
      )}

      {deferredSectionsReady && (
        <>
          <Suspense fallback={<HomeDeferredSectionSkeleton />}>
            <HomeRecommendationsSection
              title={t('home.recommended_title')}
              subtitle={t('home.recommended_subtitle')}
              entidadeId={recommendationEntidadeId}
              refreshKey={recommendationRefreshKey}
              onProductClick={onProductClick}
              onAddToCart={onAddToCart}
              isLoggedIn={isLoggedIn}
              allowGuestCheckout={allowGuestCheckout}
              loadPersonalizedFeed={!isOfferSectionsLoading}
              fallbackProducts={recommendationFallbackProducts}
            />
          </Suspense>

          {(isOfferSectionsLoading || limitedLaunchFeedProducts.length > 0) && (
            <section
              className="pb-12"
              style={{ contentVisibility: 'auto', containIntrinsicSize: '980px' }}
            >
              <div className="container mx-auto px-4">
                <div className="space-y-4">
                  {isOfferSectionsLoading ? (
                    <div className="h-7 w-52 rounded-md bg-muted/60 animate-pulse" />
                  ) : (
                    <div className="flex items-center gap-2">
                      <Sparkle className="h-5 w-5 text-[#f27a13]" weight="fill" />
                      <h2 className="text-xl font-semibold text-foreground">{t('home.offer_sections.combo_title')}</h2>
                    </div>
                  )}

                  <div className="space-y-4">
                    <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
                      {isOfferSectionsLoading
                        ? Array.from({ length: LAUNCH_FEED_INITIAL_ITEMS }).map((_, index) => (
                            <ProductCardSkeleton key={`launch-feed-skeleton-${index}`} viewMode="grid" flushImage />
                          ))
                        : visibleLaunchProducts.map((product, index) => (
                            <ProductCard
                              key={`launch-feed-${product.id}-${index}`}
                              product={product}
                              onClick={() => onProductClick(product)}
                              onAddToCart={() => onAddToCart(product)}
                              linkHref={buildProductHref(product)}
                              maxNameLines={3}
                              viewMode="grid"
                              flushImage
                              showRatings
                              showStockBadge
                              hideLoginBadge
                              isLoggedIn={isLoggedIn}
                              allowGuestCheckout={allowGuestCheckout}
                            />
                          ))}
                    </div>
                  </div>

                  {!isOfferSectionsLoading && hasMoreLaunchProducts && (
                    <div ref={launchObserverTarget} className="flex justify-center py-4">
                      {isLoadingMoreLaunchProducts && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <CircleNotch className="h-5 w-5 animate-spin" />
                          <span>{t('products.loading_more')}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  )
}
